import { filterOptions, PickerInput, type PickerOption } from './picker-input'
import type { Person } from './types'

/**
 * Who did it: suggests the organization's saved people first, then names already used on this
 * farm's logs ("Used before"). Anything typed is kept as typed, and a new name is put on the saved
 * list when the entry is recorded. Wrap it in a Catalyst `Field` with a `Label`.
 */
export function PersonNameInput({
  people,
  pastNames,
  value,
  onChange,
  ...inputProps
}: {
  people: Person[]
  pastNames: string[]
  value: string
  onChange: (name: string) => void
} & Pick<React.ComponentPropsWithoutRef<'input'>, 'placeholder' | 'autoFocus'>) {
  const known = new Set(people.map((p) => p.name.toLowerCase()))
  const saved: PickerOption[] = people.map((p) => ({ id: p.id, label: p.name }))
  const usedBefore: PickerOption[] = pastNames.filter((n) => !known.has(n.toLowerCase())).map((n) => ({ id: `name:${n}`, label: n }))
  const typed = value.trim()
  const onList = known.has(typed.toLowerCase())

  return (
    <PickerInput
      {...inputProps}
      value={value}
      onChange={onChange}
      onPick={(option) => onChange(option.label)}
      groups={[
        { label: 'People', options: filterOptions(saved, value) },
        { label: 'Used before', options: filterOptions(usedBefore, value) },
      ]}
      linked={typed && onList ? 'On your people list' : null}
      hint={typed && !onList ? 'New name: it will be added to your people list.' : undefined}
      listLabel="Saved people and names used before"
      emptyText={people.length === 0 && pastNames.length === 0 ? 'No one saved yet. Type the name.' : 'No match. Keep typing the name.'}
      autoComplete="off"
    />
  )
}
